import React, { useCallback } from 'react';
import { useDropzone, FileRejection } from 'react-dropzone';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import LinearProgress from '@mui/material/LinearProgress';
import Alert from '@mui/material/Alert';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

import { useFileUpload } from '../../hooks/useFileUpload';
import { useImportWizardStore } from '../../store/useImportWizardStore';

/**
 * react-dropzone accept configuration for CSV, XLSX, and XLS files.
 */
const DROPZONE_ACCEPT = {
  'text/csv': ['.csv'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
    '.xlsx',
  ],
  'application/vnd.ms-excel': ['.xls'],
};

interface FileDropZoneProps {
  onUploadComplete: () => void;
}

/**
 * FileDropZone provides a drag-and-drop area for file upload.
 * Shows progress bar during upload and error messages for rejected files.
 */
const FileDropZone: React.FC<FileDropZoneProps> = ({ onUploadComplete }) => {
  const { upload, progress, isUploading, error, estimatedTime } =
    useFileUpload();
  const [rejectionError, setRejectionError] = React.useState<string | null>(
    null
  );

  const onDrop = useCallback(
    async (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      setRejectionError(null);

      if (fileRejections.length > 0) {
        setRejectionError(
          'Invalid file type. Please upload a CSV, XLSX, or XLS file.'
        );
        return;
      }

      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        await upload(file);
        // After upload, check if fileID was set in the store (indicates success)
        const fileID = useImportWizardStore.getState().fileID;
        if (fileID) {
          onUploadComplete();
        }
      }
    },
    [upload, onUploadComplete]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: DROPZONE_ACCEPT,
    multiple: false,
    disabled: isUploading,
  });

  return (
    <Box>
      <Box
        {...getRootProps()}
        data-testid="file-drop-zone"
        role="button"
        aria-label="File upload drop zone. Press Enter or Space to select a file."
        tabIndex={0}
        sx={{
          border: '2px dashed',
          borderColor: isDragActive ? 'primary.main' : 'grey.400',
          borderRadius: 2,
          p: 4,
          textAlign: 'center',
          cursor: isUploading ? 'not-allowed' : 'pointer',
          backgroundColor: isDragActive ? 'action.hover' : 'background.paper',
          transition: 'border-color 0.2s, background-color 0.2s',
          opacity: isUploading ? 0.6 : 1,
        }}
      >
        <input {...getInputProps()} aria-label="File input" />
        <CloudUploadIcon
          sx={{ fontSize: 48, color: 'grey.500', mb: 1 }}
          aria-hidden="true"
        />
        {isDragActive ? (
          <Typography variant="body1" color="primary">
            Drop the file here...
          </Typography>
        ) : (
          <>
            <Typography variant="body1" gutterBottom>
              Drag and drop a file here, or click to select
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Accepted formats: CSV, XLSX, XLS
            </Typography>
          </>
        )}
      </Box>

      {/* Upload progress */}
      {isUploading && (
        <Box sx={{ mt: 2 }} aria-live="polite">
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Box sx={{ flex: 1, mr: 1 }}>
              <LinearProgress
                variant="determinate"
                value={progress}
                aria-label="Upload progress"
                aria-valuenow={progress}
                aria-valuemin={0}
                aria-valuemax={100}
              />
            </Box>
            <Typography variant="body2" color="text.secondary">
              {progress}%
            </Typography>
          </Box>
          {estimatedTime !== null && (
            <Typography variant="caption" color="text.secondary">
              Estimated time remaining: {estimatedTime}s
            </Typography>
          )}
        </Box>
      )}

      {/* Rejection error */}
      {rejectionError && (
        <Alert severity="error" sx={{ mt: 2 }} data-testid="rejection-error">
          {rejectionError}
        </Alert>
      )}

      {/* Upload error */}
      {error && (
        <Alert severity="error" sx={{ mt: 2 }} data-testid="upload-error">
          {error}
        </Alert>
      )}
    </Box>
  );
};

export default FileDropZone;
